import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { jwtDecode } from "jwt-decode";
import { fetchBearerToken } from "../api/auth.api";
import { fetchUserRights } from "../api/userRights.api";
import { RoleIdEnum, UserRights } from "../api/models/userRights.models";
import { useTranslation } from "react-i18next";

export enum UserRole {
  Read = "Read",
  Write = "Write",
  Admin = "Admin",
}

const AuthContext = createContext({
  logout: () => {},
  userName: "",
  isAdmin: false, // company specific admin role
  isLoggedIn: false,
  isLoadingUser: false,
  companyRights: [] as UserRights[],
  warnUnauthorized: () => {},
  warnForbidden: () => {},
  sessionExpired: false,
  confirmSessionExpired: () => {},
  isAdminForSelectedCompanyOrOrganization: () => false as boolean,
  canReadReportsForSelectedCompanyOrOrganization: () => false as boolean,
  canCreateReportsForSelectedCompanyOrOrganization: () => false as boolean,
  isProductCreator: false,
  isReportCreator: false,
  companyRightsHasLoaded: false,
});

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [searchParams, setParams] = useSearchParams();
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true);
  const [userName, setUserName] = useState<string>("");
  const [userRoles, setUserRole] = useState<UserRole[]>([]);
  const [companyRights, setCompanyRights] = useState<UserRights[]>([]); // what roles user has in current company context
  const [companyRightsHasLoaded, setCompanyRightsHasLoaded] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [
    azureAdCode,
    setAzureAdCode,
    bearerToken,
    setBearerToken,
    isLoggedIn,
    setIsLoggedIn,
    setOrganizationId,
    setCompanyId,
    setCompanyName,
    setOrganizationName,
    toast,
    companyId,
    organizationId,
    sessionExpired,
    setSessionExpired,
    postLoginRedirectPath,
    setPostLoginRedirectPath,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.azureAdCode,
      state.setAzureAdCode,
      state.bearerToken,
      state.setBearerToken,
      state.isLoggedIn,
      state.setIsLoggedIn,
      state.setOrganizationId,
      state.setCompanyId,
      state.setCompanyName,
      state.setOrganizationName,
      state.toastRef,
      state.companyId,
      state.organizationId,
      state.sessionExpired,
      state.setSessionExpired,
      state.postLoginRedirectPath,
      state.setPostLoginRedirectPath,
    ]),
  );

  const code = searchParams.get("code");

  const getRolesFromToken = (roleInput?: string[] | string): UserRole[] => {
    const allRoles: string[] = [];
    if (Array.isArray(roleInput)) {
      allRoles.push(...roleInput);
    } else if (typeof roleInput === "string") {
      // for microsoft identity, roles are space separated
      // e.g. "Sumi.Read Sumi.Write"
      const stringRoles = roleInput.split(" ");
      stringRoles.forEach((role) => {
        allRoles.push(role);
      });
    }

    try {
      const roles: UserRole[] = [];
      if (allRoles) {
        allRoles?.forEach((role) => {
          switch (role) {
            case "Sumi.Read":
              roles.push(UserRole.Read);
              break;
            case "Sumi.Write":
              roles.push(UserRole.Write);
              break;
            case "Sumi.Admin":
              roles.push(UserRole.Admin);
              break;
            default:
              console.warn(`Unknown role: ${role}`);
          }
        });
      }

      return roles;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return [];
    }
  };

  // always load user at startup
  useEffect(() => {
    setIsLoadingUser(true);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsLoadingUser(false);
    }
  }, [isLoggedIn]);

  // Watch for search params changes, save azure code to store
  useEffect(() => {
    if (code) {
      if (!azureAdCode && code) {
        setAzureAdCode(code as string);
        setParams({});
      }
    }
  }, [code]);

  // watch for azure ad code, if code is present, do api call to get bearer token
  useEffect(() => {
    if (azureAdCode) {
      setIsLoadingUser(true);
      fetchBearerToken(azureAdCode)
        .then((data) => {
          if (data && data.access_token) {
            setBearerToken(data.access_token);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch bearer token:", error);
        })
        .finally(() => {
          setIsLoadingUser(false);
        });
    }
  }, [azureAdCode]);

  // Once logged in, return the user to the path saved when their session expired (if any)
  useEffect(() => {
    if (isLoggedIn && postLoginRedirectPath) {
      const target =
        postLoginRedirectPath === "/login" ? "/" : postLoginRedirectPath;
      setPostLoginRedirectPath(null);
      navigate(target, { replace: true });
    }
  }, [isLoggedIn]);

  // Watch for bearer token changes, set isLoggedIn to true
  useEffect(() => {
    if (bearerToken) {
      setIsLoggedIn(true);
      setIsLoadingUser(false);

      const decoded: { name: string; nameid: string; scp: string[] | string } =
        jwtDecode(bearerToken);

      const roles = getRolesFromToken(decoded.scp);

      setUserName(decoded.name || decoded.nameid);
      setUserRole(roles);
      setIsLoggedIn(true);

      fetchUserRights(bearerToken).then((rights) => {
        setCompanyRights(rights);
        setCompanyRightsHasLoaded(true);
      });
    } else {
      setIsLoggedIn(false);
      setUserName("");
    }
  }, [bearerToken]);

  const isAdmin = userRoles.includes(UserRole.Admin);

  const logout = () => {
    setIsLoggedIn(false);
    setBearerToken(undefined);
    setAzureAdCode(undefined);
    setOrganizationId(null);
    setCompanyId(null);
    setCompanyName(null);
    setOrganizationName(null);

    // Only navigate to login if we're not already there
    if (location.pathname !== "/login") {
      navigate("/login");
      window.location.reload();
    }
  };

  const warnUnauthorized = () => {
    // ignore 401s from a tab that was never logged in, and de-duplicate concurrent 401s
    if (!isLoggedIn || sessionExpired) return;
    setPostLoginRedirectPath(location.pathname + location.search);
    setSessionExpired(true);
  };

  // called when the user acknowledges the session-expired dialog
  const confirmSessionExpired = () => {
    setSessionExpired(false);
    logout();
  };

  const warnForbidden = () => {
    toast?.current?.show({
      severity: "error",
      detail: t("error-forbidden-detail"),
      life: 8000,
      closable: true,
    });
  };

  const isCompanyAdmin = (companyId: number) => {
    return companyRights.some(
      (r) =>
        r.companyId === companyId &&
        r.permissions.some((p) => p.id === RoleIdEnum.Admin),
    );
  };

  const isOrganizationAdmin = (organizationId: number) => {
    return companyRights.some(
      (r) =>
        r.organizationId === organizationId &&
        r.permissions.some((p) => p.id === RoleIdEnum.Admin),
    );
  };

  const isAdminForSelectedCompanyOrOrganization = () => {
    if (companyId) {
      return isCompanyAdmin(companyId);
    } else if (organizationId) {
      return isOrganizationAdmin(organizationId);
    }
    return false;
  };

  const canReadReportsForSelectedCompanyOrOrganization = () => {
    // Check organization first since when org is selected, both companyId and organizationId are set
    if (organizationId) {
      return companyRights.some(
        (r) =>
          r.organizationId === organizationId ||
          (r.companyId === companyId &&
            r.organizationId === null &&
            r.permissions.some(
              (p) =>
                p.id === RoleIdEnum.ReportReader ||
                p.id === RoleIdEnum.ReportCreator ||
                p.id === RoleIdEnum.Admin ||
                p.id === RoleIdEnum.SuperAdmin,
            )),
      );
    }

    if (companyId) {
      return companyRights.some(
        (r) =>
          r.companyId === companyId &&
          r.organizationId === null &&
          r.permissions.some(
            (p) =>
              p.id === RoleIdEnum.ReportReader ||
              p.id === RoleIdEnum.ReportCreator ||
              p.id === RoleIdEnum.Admin ||
              p.id === RoleIdEnum.SuperAdmin,
          ),
      );
    }
    return false;
  };

  const canCreateReportsForSelectedCompanyOrOrganization = () => {
    // Check organization first since when org is selected, both companyId and organizationId are set
    if (organizationId) {
      return companyRights.some(
        (r) =>
          r.organizationId === organizationId ||
          (r.companyId === companyId &&
            r.organizationId === null &&
            r.permissions.some(
              (p) =>
                p.id === RoleIdEnum.ReportCreator ||
                p.id === RoleIdEnum.Admin ||
                p.id === RoleIdEnum.SuperAdmin,
            )),
      );
    }

    if (companyId) {
      return companyRights.some(
        (r) =>
          r.companyId === companyId &&
          r.organizationId === null &&
          r.permissions.some(
            (p) =>
              p.id === RoleIdEnum.ReportCreator ||
              p.id === RoleIdEnum.Admin ||
              p.id === RoleIdEnum.SuperAdmin,
          ),
      );
    }
    return false;
  };

  // TO DO - implement refresh token here

  return (
    <AuthContext.Provider
      value={{
        logout,
        warnUnauthorized,
        warnForbidden,
        sessionExpired,
        confirmSessionExpired,
        userName,
        isAdmin,
        isLoggedIn,
        isLoadingUser,
        isAdminForSelectedCompanyOrOrganization,
        canReadReportsForSelectedCompanyOrOrganization,
        canCreateReportsForSelectedCompanyOrOrganization,
        companyRights,
        isProductCreator: companyRights.some((r) =>
          r.permissions.some(
            (p) =>
              p.id === RoleIdEnum.Write ||
              p.id === RoleIdEnum.Admin ||
              p.id === RoleIdEnum.SuperAdmin,
          ),
        ),
        isReportCreator: companyRights.some((r) =>
          r.permissions.some(
            (p) =>
              p.id === RoleIdEnum.ReportCreator ||
              p.id === RoleIdEnum.Admin ||
              p.id === RoleIdEnum.SuperAdmin,
          ),
        ),
        companyRightsHasLoaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
